import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  CircularProgress,
  Box,
  Typography,
  ButtonGroup,
  Menu,
  MenuItem,
  Tooltip,
  IconButton,
} from "@mui/material";
import { GroupAdd, ArrowDropDown } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { DoneAll, Pause, PlayArrow } from "@mui/icons-material";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import BranchTallyScanForm from "./BranchTallyScanForm";
import {
  useScanShipmentMutation,
  useCompleteUserTallyMutation,
  useCompleteBranchTallyMutation,
  useGetBranchTallyDetailQuery,
  useHoldBranchTallyMutation,
  useResumeBranchTallyMutation,
} from "store/services/branchTallyApi";
import { useBranchTallySocket } from "hooks/useBranchTallySocket";
import { calcActiveDuration } from "utils/tallyDuration";

const LiveDuration = ({ tally }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [display, setDisplay] = useState("0h 0m 0s");

  useEffect(() => {
    if (!tally?.startedAt) return;
    setDisplay(calcActiveDuration(tally));
    const interval = setInterval(
      () => setDisplay(calcActiveDuration(tally)),
      1000,
    );
    return () => clearInterval(interval);
  }, [tally.isOnHold, tally.pausedAt, tally.totalPausedMs, tally?.startedAt, tally]);

  const bgColor = tally?.isOnHold ? "#fef3c7" : isDark ? "#0f172a" : "#f8fafc";
  const borderColor = tally?.isOnHold
    ? "#fcd34d"
    : isDark
    ? "transparent"
    : "#e2e8f0";
  const textColor = tally?.isOnHold
    ? "#92400e"
    : isDark
    ? "#34d399"
    : "#475569";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        background: bgColor,
        border: `1px solid ${borderColor}`,
        borderRadius: "10px",
        padding: "7px 16px",
      }}
    >
      <Box
        component="span"
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: tally?.isOnHold ? "#f59e0b" : "#34d399",
          display: "inline-block",
          animation: tally?.isOnHold ? "none" : "blink 1.2s infinite",
        }}
      />
      <Typography
        component="span"
        sx={{
          fontFamily: "monospace",
          fontSize: 18,
          fontWeight: 800,
          color: textColor,
          letterSpacing: 1,
        }}
      >
        {display}
      </Typography>
      {tally?.isOnHold && (
        <Typography
          component="span"
          sx={{ fontSize: 11, fontWeight: 700, color: "#92400e" }}
        >
          ON HOLD
        </Typography>
      )}
      <style>{`@keyframes blink{0%,100%{opacity:1}50%{opacity:0.3}}`}</style>
    </Box>
  );
};

const CompleteDialog = ({
  open,
  mode = "mine",
  onConfirm,
  onCancel,
  stats,
  completing,
}) => {
  if (!open) return null;
  const isAll = mode === "all";
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1300,
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 16,
          padding: 32,
          maxWidth: 420,
          width: "90%",
          boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ fontSize: 48, lineHeight: 1, marginBottom: 12 }}>
            {isAll ? "⚠️" : "✔️"}
          </div>
          <h3
            style={{
              margin: "0 0 6px",
              fontSize: 20,
              fontWeight: 800,
              color: "#0f172a",
            }}
          >
            {isAll ? "Complete Tally for Everyone?" : "Complete Your Tally?"}
          </h3>
          <p style={{ margin: 0, color: "#64748b", fontSize: 14 }}>
            {isAll
              ? "This immediately finalizes the tally for all participants, even if some haven't finished scanning. This cannot be undone."
              : "This marks your part done. If other participants haven't finished, the tally stays open until they do."}
          </p>
        </div>
        <div
          style={{
            background: "#f8fafc",
            borderRadius: 10,
            padding: "14px 18px",
            marginBottom: 24,
          }}
        >
          {[
            {
              label: "Active Duration",
              value: stats.duration,
              color: "#0f172a",
            },
            {
              label: "Total Scanned",
              value: stats.totalScanned,
              color: "#d97706",
            },
            { label: "Tallied", value: stats.talliedCount, color: "#059669" },
            {
              label: "Not in System",
              value: stats.missingSystemCount,
              color: "#9333ea",
            },
          ].map((r) => (
            <div
              key={r.label}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "6px 0",
                borderBottom: "1px solid #f1f5f9",
              }}
            >
              <span style={{ color: "#64748b", fontSize: 13 }}>{r.label}</span>
              <span
                style={{
                  fontWeight: 800,
                  fontFamily: "monospace",
                  color: r.color,
                  fontSize: 15,
                }}
              >
                {r.value ?? 0}
              </span>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Button variant="outlined" onClick={onCancel} disabled={completing}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={isAll ? "error" : "success"}
            startIcon={<DoneAll />}
            onClick={onConfirm}
            disabled={completing}
            sx={{ fontWeight: 700 }}
          >
            {completing
              ? "Completing…"
              : isAll
              ? "Complete For Everyone"
              : "Confirm & Complete"}
          </Button>
        </div>
      </div>
    </div>
  );
};

const BranchTallyScanScreen = () => {
  const { id: tallyId } = useParams();
  const navigate = useNavigate();
  const listPath = "/app/crm/branch-tally/list";
  const currentUserId = useSelector((state) => state.auth.user?._id);

  const {
    data: tally,
    isLoading: loadingTally,
    refetch: refetchTally,
  } = useGetBranchTallyDetailQuery(tallyId, {
    skip: !tallyId,
    pollingInterval: 10000,
  });

  const [scanShipment, { isLoading: scanning }] = useScanShipmentMutation();
  const [completeUserTally, { isLoading: completing }] =
    useCompleteUserTallyMutation();
  const [completeBranchTally, { isLoading: completingAll }] =
    useCompleteBranchTallyMutation();
  const [holdTally, { isLoading: holding }] = useHoldBranchTallyMutation();
  const [resumeTally, { isLoading: resuming }] = useResumeBranchTallyMutation();

  const [scanLog, setScanLog] = useState([]);
  const [lastResult, setLastResult] = useState(null);
  const [showComplete, setShowComplete] = useState(false);
  const [completeMode, setCompleteMode] = useState("mine"); // "mine" | "all"
  const [menuAnchor, setMenuAnchor] = useState(null);

  const isOnHold = tally?.isOnHold ?? false;
  const myParticipant = tally?.participants?.find(
    (p) => p._id?.toString() === currentUserId?.toString(),
  );
  const iAmDone = !!myParticipant?.completedAt;

  const stats = {
    duration: calcActiveDuration(tally),
    totalScanned: scanLog.length,
    talliedCount: scanLog.filter((s) => s.type === "tallied").length,
    missingSystemCount: scanLog.filter((s) => s.type === "missingSystem")
      .length,
  };

  // Hydrate scanLog from server data on initial load / refresh
  useEffect(() => {
    if (!tally || scanLog.length > 0) return;

    const talliedRows = (tally.talliedOrders ?? []).map((r) => ({
      _id: r._id,
      shipmentId: r.shipmentId,
      type: "tallied",
      time: r.scanTime,
      scannedByName:
        r.scannedBy?._id === currentUserId ? "You" : r.scannedBy?.name,
    }));

    const missingRows = (tally.missingSystem ?? []).map((r) => ({
      _id: r._id,
      shipmentId: r.scannedShipmentId,
      type: "missingSystem",
      time: r.scanTime,
      scannedByName:
        r.scannedBy?._id === currentUserId ? "You" : r.scannedBy?.name,
    }));

    const combined = [...talliedRows, ...missingRows].sort(
      (a, b) => new Date(b.time) - new Date(a.time),
    );

    if (combined.length) setScanLog(combined);
  }, [tally, currentUserId, scanLog.length]);

  useBranchTallySocket({
    tallyId,
    currentUserId,
    onScanUpdate: (data) => {
      if (data.scannedBy._id === currentUserId) return;
      setScanLog((prev) => {
        if (prev.some((s) => s._id === data.scanId)) return prev;
        return [
          {
            _id: data.scanId ?? Date.now(),
            shipmentId: data.shipmentId,
            type: data.type,
            time: data.scanTime ?? new Date().toISOString(),
            scannedByName: data.scannedBy.name,
          },
          ...prev,
        ];
      });
    },
    onCompleted: (data) => {
      if (data.fullyCompleted) {
        navigate(`/app/crm/branch-tally/${tallyId}/detail`);
      } else {
        refetchTally();
      }
    },
  });

  const handleHold = async () => {
    try {
      await holdTally(tallyId).unwrap();
      toast.info("Tally is now on hold.");
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't pause tally.");
    }
  };

  const handleResume = async () => {
    try {
      await resumeTally(tallyId).unwrap();
      toast.success("Tally resumed.");
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't resume tally.");
    }
  };

  const handleScan = async ({ shipmentId }) => {
    if (isOnHold) {
      setLastResult({
        type: "error",
        shipmentId,
        message: "Tally is on hold. Resume before scanning.",
      });
      return;
    }
    const already = scanLog.find(
      (s) => s.shipmentId.toUpperCase() === shipmentId.toUpperCase(),
    );
    if (already) {
      setLastResult({
        type: "duplicate",
        shipmentId,
        message: `Already scanned at ${new Date(
          already.time,
        ).toLocaleTimeString()}`,
      });
      return;
    }
    try {
      const result = await scanShipment({
        tallyId,
        scannedShipmentId: shipmentId,
      }).unwrap();
      setScanLog((prev) => [
        {
          _id: result.scan?._id ?? Date.now(),
          shipmentId,
          type: result.type,
          time: result.scan?.scanTime ?? new Date().toISOString(),
          scannedByName: "You",
        },
        ...prev,
      ]);
      setLastResult({ type: result.type, shipmentId, message: result.message });
    } catch (err) {
      if (err?.status === 409) {
        toast.error(err?.data?.message ?? "Tally already completed.");
        navigate(listPath);
        return;
      }
      if (err?.status === 403) {
        await refetchTally();
        setLastResult({ type: "error", shipmentId, message: err.data.message });
        return;
      }
      setLastResult({
        type: "error",
        shipmentId,
        message: err?.data?.message ?? "Scan failed. Please try again.",
      });
    }
  };

  const handleComplete = async () => {
    try {
      if (completeMode === "all") {
        await completeBranchTally(tallyId).unwrap();
        toast.success("Tally completed for all participants.");
        navigate(`/app/crm/branch-tally/${tallyId}/detail`);
      } else {
        const res = await completeUserTally(tallyId).unwrap();
        await refetchTally();
        if (res.fullyCompleted) {
          toast.success("All participants done! Tally report is ready.");
          navigate(`/app/crm/branch-tally/${tallyId}/detail`);
        } else {
          toast.success("Your part is marked complete. Waiting for others.");
        }
      }
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't complete tally.");
    } finally {
      setShowComplete(false);
    }
  };

  return (
    <>
      <BreadcrumbLayout
        breadcrumbs={[
          { title: "Reports", path: "/app/crm/report" },
          { title: "Branch Tally", path: listPath },
          { title: "Scan" },
        ]}
        isBusy={loadingTally || completing || completingAll}
        headerActions={
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {tally && <LiveDuration tally={tally} />}
            <Tooltip title="Add Participants" arrow>
              <IconButton
                size="small"
                color="primary"
                onClick={() =>
                  navigate(`/app/crm/branch-tally/${tallyId}/update`)
                }
              >
                <GroupAdd />
              </IconButton>
            </Tooltip>
            {isOnHold ? (
              <Button
                variant="contained"
                color="warning"
                size="small"
                startIcon={
                  resuming ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <PlayArrow />
                  )
                }
                disabled={resuming}
                onClick={handleResume}
                sx={{ fontWeight: 700 }}
              >
                {resuming ? "Resuming…" : "Resume"}
              </Button>
            ) : (
              <Button
                variant="outlined"
                color="warning"
                size="small"
                startIcon={
                  holding ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <Pause />
                  )
                }
                disabled={holding}
                onClick={handleHold}
                sx={{ fontWeight: 700 }}
              >
                {holding ? "Pausing…" : "Hold"}
              </Button>
            )}
            <ButtonGroup variant="contained" color="success" size="small">
              <Button
                startIcon={<DoneAll />}
                disabled={completing || completingAll || isOnHold || iAmDone}
                onClick={() => {
                  setCompleteMode("mine");
                  setShowComplete(true);
                }}
                sx={{ fontWeight: 700 }}
              >
                {iAmDone ? "You're Done" : "Complete My Tally"}
              </Button>
              <Button
                size="small"
                disabled={completing || completingAll || isOnHold}
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                sx={{ px: 0.5 }}
              >
                <ArrowDropDown fontSize="small" />
              </Button>
            </ButtonGroup>
            <Menu
              anchorEl={menuAnchor}
              open={!!menuAnchor}
              onClose={() => setMenuAnchor(null)}
            >
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  setCompleteMode("all");
                  setShowComplete(true);
                }}
              >
                Complete for All
              </MenuItem>
            </Menu>
          </div>
        }
      >
        {isOnHold && (
          <div
            style={{
              background: "#fef3c7",
              border: "1px solid #fcd34d",
              borderRadius: 10,
              padding: "12px 18px",
              marginBottom: 16,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span style={{ fontSize: 20 }}>⏸</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#92400e" }}>
                Tally is on hold
              </div>
              <div style={{ fontSize: 12, color: "#b45309" }}>
                Scanning is blocked. Click Resume to continue.
              </div>
            </div>
            <Button
              variant="contained"
              color="warning"
              size="small"
              startIcon={<PlayArrow />}
              onClick={handleResume}
              disabled={resuming}
              sx={{ marginLeft: "auto", fontWeight: 700 }}
            >
              Resume
            </Button>
          </div>
        )}
        <BreadcrumbLayout.Paper>
          <BranchTallyScanForm
            isBusy={scanning}
            onScan={handleScan}
            scanLog={scanLog}
            lastResult={lastResult}
            tallyInfo={tally}
            stats={stats}
            isOnHold={isOnHold || iAmDone}
          />
        </BreadcrumbLayout.Paper>
      </BreadcrumbLayout>
      <CompleteDialog
        open={showComplete}
        mode={completeMode}
        stats={stats}
        onConfirm={handleComplete}
        onCancel={() => setShowComplete(false)}
        completing={completing || completingAll}
      />
    </>
  );
};

export default BranchTallyScanScreen;
